/** Parasmaipad + ātmanepad kartari-style rūpa fields (full). */
export const TINGANTA_RUP_KARTARI_FIELD_KEYS = [
  'plat',
  'plit',
  'plut',
  'plrut',
  'plot',
  'plang',
  'pvidhiling',
  'pashirling',
  'plung',
  'plrung',
  'alat',
  'alit',
  'alut',
  'alrut',
  'alot',
  'alang',
  'avidhiling',
  'aashirling',
  'alung',
  'alrung',
] as const;

/** Karmani / ātmanepad-only blocks (10 fields). */
export const TINGANTA_RUP_KARMANI_FIELD_KEYS = [
  'alat',
  'alit',
  'alut',
  'alrut',
  'alot',
  'alang',
  'avidhiling',
  'aashirling',
  'alung',
  'alrung',
] as const;

/** Parasmaipad-only (e.g. yaṅluk kartari). */
export const TINGANTA_RUP_PARASMAI_FIELD_KEYS = [
  'plat',
  'plit',
  'plut',
  'plrut',
  'plot',
  'plang',
  'pvidhiling',
  'pashirling',
  'plung',
  'plrung',
] as const;

export type TingantaRupKartariFieldKey = (typeof TINGANTA_RUP_KARTARI_FIELD_KEYS)[number];
export type TingantaRupKarmaniFieldKey = (typeof TINGANTA_RUP_KARMANI_FIELD_KEYS)[number];
export type TingantaRupParasmaiFieldKey = (typeof TINGANTA_RUP_PARASMAI_FIELD_KEYS)[number];

/** Devanagari column headings for parasmaipada kartari 3×3 tables (row-major in `;`-separated storage). */
export const TINGANTA_RUP_KARTARI_PARASMAI_TABLES: readonly {
  field: TingantaRupParasmaiFieldKey;
  header: string;
}[] = [
  { field: 'plat', header: 'कर्तरि लट् लकारः (परस्मैपदम्)' },
  { field: 'plit', header: 'कर्तरि लिट् लकारः (परस्मैपदम्)' },
  { field: 'plut', header: 'कर्तरि लुट् लकारः (परस्मैपदम्)' },
  { field: 'plrut', header: 'कर्तरि लृट् लकारः (परस्मैपदम्)' },
  { field: 'plot', header: 'कर्तरि लोट् लकारः (परस्मैपदम्)' },
  { field: 'plang', header: 'कर्तरि लङ् लकारः (परस्मैपदम्)' },
  { field: 'pvidhiling', header: 'कर्तरि विधिलिङ् लकारः (परस्मैपदम्)' },
  { field: 'pashirling', header: 'कर्तरि आशीर्षिलिङ् लकारः (परस्मैपदम्)' },
  { field: 'plung', header: 'कर्तरि लुङ् लकारः (परस्मैपदम्)' },
  { field: 'plrung', header: 'कर्तरि लृङ् लकारः (परस्मैपदम्)' },
] as const;

/** Ātmanepad kartari — same 3×3 storage as parasmaipada. */
export const TINGANTA_RUP_KARTARI_ATMANEPAD_TABLES: readonly {
  field: TingantaRupKarmaniFieldKey;
  header: string;
}[] = [
  { field: 'alat', header: 'कर्तरि लट् लकारः (आत्मनेपदम्)' },
  { field: 'alit', header: 'कर्तरि लिट् लकारः (आत्मनेपदम्)' },
  { field: 'alut', header: 'कर्तरि लुट् लकारः (आत्मनेपदम्)' },
  { field: 'alrut', header: 'कर्तरि लृट् लकारः (आत्मनेपदम्)' },
  { field: 'alot', header: 'कर्तरि लोट् लकारः (आत्मनेपदम्)' },
  { field: 'alang', header: 'कर्तरि लङ् लकारः (आत्मनेपदम्)' },
  { field: 'avidhiling', header: 'कर्तरि विधिलिङ् लकारः (आत्मनेपदम्)' },
  { field: 'aashirling', header: 'कर्तरि आशीर्षिलिङ् लकारः (आत्मनेपदम्)' },
  { field: 'alung', header: 'कर्तरि लुङ् लकारः (आत्मनेपदम्)' },
  { field: 'alrung', header: 'कर्तरि लृङ् लकारः (आत्मनेपदम्)' },
] as const;

/** Rūpa karmani — only ātmanepad-shaped fields (`alat`…); labels use कर्मणि. */
export const TINGANTA_RUP_KARMANI_TABLES: readonly {
  field: TingantaRupKarmaniFieldKey;
  header: string;
}[] = TINGANTA_RUP_KARTARI_ATMANEPAD_TABLES.map(({ field, header }) => ({
  field,
  header: header.replace('कर्तरि', 'कर्मणि'),
}));

const _SAN = 'सन्नन्ते कर्तरि';

/** San kartari — parasmaipada block (same field keys as rūpa kartari). */
export const TINGANTA_SAN_KARTARI_PARASMAI_TABLES: readonly {
  field: TingantaRupParasmaiFieldKey;
  header: string;
}[] = TINGANTA_RUP_KARTARI_PARASMAI_TABLES.map(({ field, header }) => ({
  field,
  header: header.replace('कर्तरि', _SAN),
}));

/** San kartari — ātmanepad block. */
export const TINGANTA_SAN_KARTARI_ATMANEPAD_TABLES: readonly {
  field: TingantaRupKarmaniFieldKey;
  header: string;
}[] = TINGANTA_RUP_KARTARI_ATMANEPAD_TABLES.map(({ field, header }) => ({
  field,
  header: header.replace('कर्तरि', _SAN),
}));

const _SAN_KRM = 'सन्नन्ते कर्मणि';

/** San karmani — ten lakārāḥ, कर्मणि + सन्. */
export const TINGANTA_SAN_KARMANI_TABLES: readonly {
  field: TingantaRupKarmaniFieldKey;
  header: string;
}[] = TINGANTA_RUP_KARMANI_TABLES.map(({ field, header }) => ({
  field,
  header: header.replace('कर्मणि', _SAN_KRM),
}));

const _NIJ_KR = 'णिजन्ते कर्तरि';
const _NIJ_KRM = 'णिजन्ते कर्मणि';

/** Ṇij kartari — parasmaipada. */
export const TINGANTA_NICH_KARTARI_PARASMAI_TABLES: readonly {
  field: TingantaRupParasmaiFieldKey;
  header: string;
}[] = TINGANTA_RUP_KARTARI_PARASMAI_TABLES.map(({ field, header }) => ({
  field,
  header: header.replace('कर्तरि', _NIJ_KR),
}));

/** Ṇij kartari — ātmanepad. */
export const TINGANTA_NICH_KARTARI_ATMANEPAD_TABLES: readonly {
  field: TingantaRupKarmaniFieldKey;
  header: string;
}[] = TINGANTA_RUP_KARTARI_ATMANEPAD_TABLES.map(({ field, header }) => ({
  field,
  header: header.replace('कर्तरि', _NIJ_KR),
}));

/** Ṇij karmani. */
export const TINGANTA_NICH_KARMANI_TABLES: readonly {
  field: TingantaRupKarmaniFieldKey;
  header: string;
}[] = TINGANTA_RUP_KARMANI_TABLES.map(({ field, header }) => ({
  field,
  header: header.replace('कर्मणि', _NIJ_KRM),
}));
